/** Live-chat routing (M29 Phase 2): presence, chat limits, offers and the waiting queue. Shell created by WP-M29-SCHEMA2: later work packages add keys inside their group (en and fil together, no dots inside key segments). */
export const routingLive = {
  presence: {
    trigger: "Chat status: {{status}}, {{count}} of {{limit}} chats",
    menu: "Your chat status",
    online: "Online",
    away: "Away",
    busy: "Busy",
    offline: "Offline",
    autoAway: "Away (inactive)",
    hintOnline: "New chats are offered to you.",
    hintAway: "No new chats. The ones you hold stay with you.",
    hintBusy: "No new chats while you finish the ones you hold.",
    hintOffline: "You are not available for chats.",
    chats: "Chats {{count}} / {{limit}}",
    atLimit: "At your chat limit",
    autoAwayNotice: "You were set to Away after a few minutes without activity. Any activity brings you back to Online.",
    changed: "You are {{status}}",
    changeFailed: "Could not change your status. Try again.",
    loadFailed: "Could not load your chat status. Try again.",
    loading: "Loading your chat status",
  },
  limits: {
    title: "Chat limits",
    intro: "How many chats each person and team can hold at once. Someone who is not online, or is at their limit, is skipped when a chat is assigned.",
    agentsTitle: "Agents and limits",
    agentColumn: "Agent",
    statusColumn: "Status",
    teamsColumn: "Teams",
    chatsColumn: "Chats",
    limitColumn: "Limit",
    memberLimit: "Chat limit for {{name}}",
    noTeams: "No team",
    lowered: "Lowered to {{limit}} by a team cap",
    teamsTitle: "Team caps",
    teamsIntro: "A team cap lowers the limit of every member of the team. Leave it empty to use their own limits.",
    teamLimit: "Chat cap for {{name}}",
    teamPlaceholder: "No cap",
    save: "Save limits",
    reset: "Discard changes",
    saved: "Chat limits saved",
    saveFailed: "Could not save the chat limits.",
    invalid: "Enter a whole number from {{min}} to {{max}}.",
    emptyTitle: "No one can take chats yet",
    emptyBody: "Give a teammate a role that can reply in the inbox and they appear here.",
    loadFailed: "The chat limits could not be loaded.",
    retry: "Try again",
    readOnly: "You can view the limits. Changing them needs permission to manage live chat.",
    loading: "Loading chat limits",
  },
  offers: {
    region: "Chat offers",
    title: "A chat is waiting for you",
    team: "Team {{team}}",
    attempt: "Offer {{n}}",
    accept: "Accept",
    decline: "Pass",
    secondsLeft: "{{seconds}} s left",
    announce: "A chat is offered to you. Accept within {{seconds}} seconds.",
    shortcuts: "Accept {{accept}}, pass {{decline}}",
    taken: "You took the chat.",
    passed: "You passed. The chat goes to the next agent.",
    gone: "That chat is no longer offered to you.",
    atLimit: "You are at your chat limit. Finish a chat, then try again.",
    answerFailed: "Could not answer the offer. Try again.",
    settingsTitle: "Chat offers",
    settingsIntro: "Offer each waiting chat to one agent first. If they do not accept within the time you set, it moves to the next agent who can take it.",
    switchLabel: "Offer each chat to one agent first",
    switchHint: "Off: a chat is assigned straight to the next available agent.",
    timeoutLabel: "Seconds to accept",
    timeoutHint: "From {{min}} to {{max}} seconds. The default is {{default}}.",
    summary: "Accept within {{seconds}} s, then offer the next agent",
    summaryOff: "Chats are assigned straight to the next available agent",
    neverOver: "A chat is only offered to an agent who is online and under their chat limit. Someone who passed or let it lapse is not offered the same chat again.",
    invalid: "Enter a whole number from {{min}} to {{max}}.",
    save: "Save offer settings",
    reset: "Discard changes",
    saved: "Offer settings saved",
    saveFailed: "Could not save the offer settings.",
    loadFailed: "The offer settings could not be loaded.",
    retry: "Try again",
    readOnly: "You can view the offer settings. Changing them needs permission to manage live chat.",
    loading: "Loading the offer settings",
  },
  queue: {
    pageTitle: "Live chat routing",
    pageIntro: "Who answers a waiting chat first, and how long it may wait. These rules apply to chats that wait for a person.",
    reset: "Discard changes",
    retry: "Try again",
    loading: "Loading",
    readOnly: "You can view these settings. Changing them needs permission to manage live chat.",
    tiers: {
      title: "Tiers and waiting",
      intro: "A waiting chat scores its tier's weight plus the aging points below for every minute it waits. The highest score is served first, so nobody waits for ever.",
      tierColumn: "Tier",
      weightColumn: "Weight",
      weightLabel: "Weight for {{tier}}",
      noTier: "Everyone else",
      addLabel: "Add a tier",
      addPlaceholder: "tier code, e.g. gold",
      addButton: "Add tier",
      agingLabel: "Aging points per waiting minute",
      agingHint: "From 0 to {{max}}. With 0 a higher tier always goes first.",
      invalidAging: "Enter a number from 0 to {{max}} with at most two decimals.",
      invalidWeight: "Enter a whole number from {{min}} to {{max}}.",
      overtake: "A chat with no tier is served before a new {{high}} chat once it has waited {{minutes}} min.",
      overtakeNever: "With no aging a chat from a lower tier can wait for ever behind a higher one.",
      noWeights: "Give a tier a weight to see how long other chats wait before they overtake it.",
      safety: "Chats from a required handover (such as responsible gaming) are always served before every tier, whatever the weights.",
      save: "Save tiers",
      saved: "Tier weights saved",
      saveFailed: "Could not save the tier weights.",
    },
    ladder: {
      title: "Who gets it, in order",
      intro: "For customers whose tier or segment carries weight. A step nobody can take is passed over at once.",
      rungHost: "Their dedicated host",
      rungHostHint: "For {{seconds}} s, if online and under their chat limit",
      rungTeam: "The team the chat was routed to",
      rungTeamHint: "After {{seconds}} s",
      rungGeneral: "Everybody who can reply",
      rungGeneralHint: "After {{seconds}} s, top of the line",
      hostLabel: "Seconds the host has it first",
      teamLabel: "Seconds the team has it next",
      invalidSeconds: "Enter a whole number from {{min}} to {{max}}.",
      hostSource: "The host is named in the customer's dedicated_host attribute (an e-mail address or a member), sent by your system with the customer context.",
      never: "This never overrides a required handover: those chats stay with their own team.",
      save: "Save order",
      saved: "Routing order saved",
      saveFailed: "Could not save the routing order.",
    },
    skills: {
      title: "Chat skills",
      intro: "A chat that needs a skill (the customer's language, responsible-gaming training, a segment) is only offered to people who hold it.",
      listLabel: "Skills in this workspace",
      listHint: "Lowercase letters, digits and underscores, separated by commas. Up to {{max}}. Use rg_trained for responsible-gaming training.",
      invalidList: "Use lowercase letters, digits and underscores (up to 40 each), at most {{max}} skills.",
      listSave: "Save skills list",
      listSaved: "Skills list saved",
      membersTitle: "Who holds which skill",
      memberColumn: "Agent",
      skillsColumn: "Skills",
      skillsOf: "Skills of {{name}}",
      membersSave: "Save agent skills",
      membersSaved: "Agent skills saved",
      saveFailed: "Could not save the skills.",
      loadFailed: "The agent skills could not be loaded.",
      emptyTitle: "No skills listed yet",
      emptyBody: "Add skills above, then mark which agents hold them.",
    },
    rules: {
      title: "Segment rules",
      intro: "Customers in a segment get extra points on top of their tier, and the chat can need a skill. Segments are made under Contacts.",
      segmentColumn: "Segment",
      bonusColumn: "Extra points",
      skillColumn: "Needs skill",
      actionsColumn: "Actions",
      bonusLabel: "Extra points for {{segment}}",
      pick: "Choose a segment",
      noSkill: "No skill",
      add: "Add a rule",
      remove: "Remove the rule for {{segment}}",
      deleted: "(deleted segment)",
      emptyTitle: "No segment rules",
      emptyBody: "Add a rule so customers in a segment are served sooner.",
      noSegmentsTitle: "No segments yet",
      noSegmentsBody: "Create a segment under Contacts first, then use it here.",
      invalidSegment: "Pick a different segment for each rule.",
      save: "Save rules",
      saved: "Segment rules saved",
      saveFailed: "Could not save the segment rules.",
      loadFailed: "The segments could not be loaded.",
    },
    order: {
      title: "Live order",
      intro: "The chats waiting for a person right now, in the order they will be served.",
      position: "No.",
      chat: "Chat",
      chatLink: "Chat {{id}}",
      tier: "Tier",
      team: "Team",
      waited: "Waiting",
      score: "Score",
      stage: "With",
      stageHost: "Dedicated host",
      stageTeam: "Its team",
      stageGeneral: "Everybody",
      noTier: "No tier",
      safety: "Required handover",
      alwaysFirst: "Always first",
      emptyTitle: "Nobody is waiting",
      emptyBody: "Chats that wait for a person appear here, best-ranked first.",
      loadFailed: "The live order could not be loaded.",
    },
    inbox: {
      sortedBy: "Ordered by priority and waiting time",
      waiting: "Waiting {{time}}",
      scoreHint: "Score {{score}}: the tier's weight plus aging",
      firstHint: "A required handover is always served first",
    },
  },
} as const;
